/**
 * Mockup — Rediseño de la información dentro de cada Reserva.
 *
 * Basado en los esquemas dibujados a mano por Eduardo (feb-2026):
 *   • Módulo 1: DATOS (siempre visible) — tipo, descripción, ciudad, proveedor,
 *     producto, estado reserva, día, hora, flag, estado pago, facturación,
 *     factura solicitada, proforma/voucher → conciliado, notas.
 *   • Módulo 2: COTIZACIÓN (colapsable) — unidades, métrica, modelo cálculo,
 *     cotizado K/E, coste real total, s/IVA & c/IVA.
 *   • Debajo van las pestañas actuales (Pagos, Documentos, Factura IA, Solicitud,
 *     Vouchers, Recordatorios, Template, Chat) — se preservan tal cual.
 *
 * Ruta: /mockup/reserva-layout
 * NO usa datos reales del backend — todo hardcodeado para preview.
 */
import React, { useState } from "react";
import { ArrowLeft, Save, Calculator, ChevronDown, Building2, Tag, MapPin, Package, Circle, CircleCheck, ClipboardList, StickyNote, Receipt, FileSignature, ShieldCheck, Coins, Flag, ScrollText } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/* ─────────────────────────── Helpers UI ─────────────────────────── */

// Campo con label + valor (view mode) o input (edit mode).
const Field = ({ icon: Icon, label, value, children, dataTestid, span = "" }) => (
  <div className={`space-y-1 ${span}`} data-testid={dataTestid}>
    <Label className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      {Icon && <Icon className="h-3 w-3" />}
      {label}
    </Label>
    {children ?? <p className="text-sm font-medium text-foreground">{value ?? "—"}</p>}
  </div>
);

// Toggle switch compacto en línea (mismo alto que un Input h-9, para grid perfecto).
const ToggleRow = ({ icon: Icon, label, checked, onChange, dataTestid, disabled }) => (
  <div className={`flex h-9 items-center justify-between rounded-md border bg-muted/20 px-3 ${disabled ? "opacity-70" : ""}`} data-testid={dataTestid}>
    <Label className="flex items-center gap-1.5 text-xs font-medium">
      {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
      {label}
    </Label>
    <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
  </div>
);

// Etiqueta de estado con color según valor.
const StatusPill = ({ value, palette }) => {
  const cfg = palette[value] || { label: value, cls: "bg-slate-100 text-slate-700 border-slate-300" };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cfg.cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {cfg.label}
    </span>
  );
};

/* ─────────────────────────── Datos de muestra ─────────────────────────── */

const SAMPLE_TRANSFER = {
  service: "Private Transfer Lisbon Airport to Hotel",
  type: "transfer",
  type_label: "Transfer",
  provider_name: "BeDriven",
  product: "Airport → Hotel · Private (up to 3 pax)",
  city: "Lisbon",
  country: "Portugal",
  date_entry: "2026-09-28",
  hour_entry: "14:15",
  status_reserva: "confirmacion",
  status_facturacion: "factura",
  status_pago: true,
  status_conciliado: false,
  status_proforma_voucher: true,
  factura_solicitada: true,
  flag: false,
  notes: "Cliente pide silla infantil. Vuelo TP1234 aterriza 14:15 LIS.",

  // Cotización
  units: 1,
  currency: "EUR",
  override: "no",
  cost_excl: 76.86,
  cost_incl: 85.00,
  cotizado_kk: 108.00,
  cotizado_real: 108.00,
  beneficio: 23.00,
  margen_pct: 21.3,
};

const SAMPLE_ALOJAMIENTO = {
  service: "1908 Lisbon Hotel",
  type: "alojamiento",
  type_label: "Alojamiento",
  provider_name: "Beds4U",
  product: "Doble Deluxe · BB · Vista ciudad",
  city: "Lisbon",
  country: "Portugal",
  date_entry: "2026-09-28",
  date_exit: "2026-09-30",
  board: "BB",
  hour_entry: "",
  status_reserva: "reserva",
  status_facturacion: "sin_factura",
  status_pago: false,
  status_conciliado: false,
  status_proforma_voucher: false,
  factura_solicitada: false,
  flag: true,
  notes: "Preferencia planta alta. Late check-in avisado a hotel.",

  // Cotización
  units: 2, // noches
  currency: "EUR",
  override: "no",
  cost_excl: 210.50,
  cost_incl: 245.00,
  cotizado_kk: 320.00,
  cotizado_real: 320.00,
  beneficio: 75.00,
  margen_pct: 23.4,
};

const ESTADO_RESERVA_PALETTE = {
  sin_seleccion: { label: "Sin seleccionar", cls: "bg-slate-100 text-slate-600 border-slate-300" },
  contacto:      { label: "Contacto",        cls: "bg-amber-100 text-amber-800 border-amber-300" },
  reserva:       { label: "Reserva",         cls: "bg-blue-100 text-blue-800 border-blue-300" },
  confirmacion:  { label: "Confirmación",    cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  cancelada:     { label: "Cancelada",       cls: "bg-rose-100 text-rose-800 border-rose-300" },
};
const FACT_PALETTE = {
  sin_factura:        { label: "Sin factura",        cls: "bg-slate-100 text-slate-600 border-slate-300" },
  factura:            { label: "Factura",            cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  factura_mensual:    { label: "Factura mensual",    cls: "bg-blue-100 text-blue-800 border-blue-300" },
  facturado_mensual:  { label: "Facturado mensual",  cls: "bg-teal-100 text-teal-800 border-teal-300" },
  no_aplica:          { label: "No aplica",          cls: "bg-slate-100 text-slate-600 border-slate-300" },
};

const fmtEUR = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);
const fmtDate = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("es-ES", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });

/* ─────────────────────────── Componente principal ─────────────────────────── */

export default function ReservaLayoutMockup() {
  const [variant, setVariant] = useState("transfer"); // 'transfer' | 'alojamiento'
  const [state, setState] = useState(SAMPLE_TRANSFER);
  const [cotOpen, setCotOpen] = useState(true);
  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  // Cambia el ejemplo activo (reinicia el state para reflejar el otro tipo).
  const switchVariant = (v) => {
    setVariant(v);
    setState(v === "alojamiento" ? SAMPLE_ALOJAMIENTO : SAMPLE_TRANSFER);
  };

  const isAlojamiento = state.type === "alojamiento";
  const nights = (() => {
    if (!state.date_entry || !state.date_exit) return 0;
    const a = new Date(state.date_entry), b = new Date(state.date_exit);
    return Math.max(0, Math.round((b - a) / 86400000));
  })();

  return (
    <div className="mx-auto min-h-screen max-w-6xl bg-background p-6 space-y-6" data-testid="mockup-reserva-layout">
      {/* Aviso mockup + selector de variante */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900">
        <div>
          <strong>Mockup visual</strong> — Rediseño propuesto para la información dentro de cada reserva (feb-2026).
        </div>
        <div className="flex items-center gap-1 rounded-md border border-amber-400 bg-white p-0.5">
          <button
            onClick={() => switchVariant("transfer")}
            className={`rounded px-3 py-1 text-xs font-semibold transition ${variant === "transfer" ? "bg-amber-500 text-white" : "text-amber-900 hover:bg-amber-100"}`}
            data-testid="mockup-variant-transfer"
          >Transfer</button>
          <button
            onClick={() => switchVariant("alojamiento")}
            className={`rounded px-3 py-1 text-xs font-semibold transition ${variant === "alojamiento" ? "bg-amber-500 text-white" : "text-amber-900 hover:bg-amber-100"}`}
            data-testid="mockup-variant-alojamiento"
          >Alojamiento</button>
        </div>
      </div>

      {/* Cabecera de la reserva */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="h-9 w-9"><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <p className="font-heading text-lg font-bold">{state.service}</p>
            <div className="flex items-center gap-2 pt-0.5">
              <Badge variant="outline" className="text-[10px]">{state.type_label}</Badge>
              <StatusPill value={state.status_reserva} palette={ESTADO_RESERVA_PALETTE} />
            </div>
          </div>
        </div>
        <Button><Save className="mr-1.5 h-4 w-4" />Guardar</Button>
      </div>

      {/* Contexto del viaje (solo lectura — sin cambios) */}
      <Card>
        <CardContent className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-6">
          <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Lead</p><p className="text-sm font-medium">Ryan Martensen and Amanda Healy</p></div>
          <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">AD</p><p className="text-sm font-medium">2</p></div>
          <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Ch</p><p className="text-sm font-medium">0</p></div>
          <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Agente Ventas</p><p className="text-sm font-medium">Beatriz</p></div>
          <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Gestión</p><p className="text-sm font-medium">Heidi</p></div>
          <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Traveler Sup.</p><p className="text-sm font-medium">—</p></div>
        </CardContent>
      </Card>

      {/* ═══════════════════════ MÓDULO 1 · DATOS ═══════════════════════ */}
      <section className="rounded-lg border border-primary/25 bg-card shadow-sm" data-testid="mockup-modulo-datos">
        <header className="flex items-center justify-between border-b bg-gradient-to-r from-primary/5 to-transparent px-5 py-3">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-base font-bold">Datos</h2>
          </div>
          <span className="text-xs text-muted-foreground">Información de la reserva</span>
        </header>

        <div className="space-y-4 p-5">
          {/* Fila 1: Tipo Servicio (3) · Descripción (6) · Ciudad (3) */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-12">
            <Field icon={Tag} label="Tipo servicio" span="sm:col-span-3">
              <Select value={state.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="h-9" data-testid="mockup-datos-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="alojamiento">Alojamiento</SelectItem>
                  <SelectItem value="actividad">Actividad</SelectItem>
                  <SelectItem value="entradas">Entradas</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                  <SelectItem value="tren">Tren</SelectItem>
                  <SelectItem value="vuelo">Vuelo</SelectItem>
                  <SelectItem value="free_day">Free day</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field icon={ScrollText} label="Descripción" span="sm:col-span-6">
              <Input className="h-9" value={state.service} onChange={(e) => set("service", e.target.value)} data-testid="mockup-datos-descripcion" />
            </Field>
            <Field icon={MapPin} label="Ciudad" span="sm:col-span-3">
              <Input className="h-9" value={state.city} onChange={(e) => set("city", e.target.value)} data-testid="mockup-datos-ciudad" />
            </Field>
          </div>

          {/* Fila 2: Proveedor (4) · Producto (5) · Estado Reserva ⭐ (3) */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-12">
            <Field icon={Building2} label="Proveedor" span="sm:col-span-4">
              <Input className="h-9" value={state.provider_name} onChange={(e) => set("provider_name", e.target.value)} data-testid="mockup-datos-proveedor" />
            </Field>
            <Field icon={Package} label="Producto" span="sm:col-span-5">
              <Input className="h-9" value={state.product} onChange={(e) => set("product", e.target.value)} data-testid="mockup-datos-producto" />
            </Field>
            <div className="sm:col-span-3">
              <Label className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                <CircleCheck className="h-3 w-3" /> Estado Reserva
              </Label>
              <Select value={state.status_reserva} onValueChange={(v) => set("status_reserva", v)}>
                <SelectTrigger className="h-9 border-primary/40 bg-primary/5 font-semibold" data-testid="mockup-datos-estado-reserva">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ESTADO_RESERVA_PALETTE).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Fila 3 · ADAPTATIVA según tipo:
              - Alojamiento: Check-in (3) · Check-out (3) · Noches (2) · Régimen (2) · Flag (2)
              - Resto:       Día (3) · Hora (3) · Flag (3) · Estado Pago info (3)  */}
          {isAlojamiento ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-12" data-testid="mockup-datos-fila3-aloj">
              <Field icon={Circle} label="Check-in" span="sm:col-span-3">
                <Input type="date" className="h-9" value={state.date_entry} onChange={(e) => set("date_entry", e.target.value)} data-testid="mockup-datos-checkin" />
              </Field>
              <Field icon={Circle} label="Check-out" span="sm:col-span-3">
                <Input type="date" className="h-9" value={state.date_exit} onChange={(e) => set("date_exit", e.target.value)} data-testid="mockup-datos-checkout" />
              </Field>
              <Field label="Noches" span="sm:col-span-2">
                <Input readOnly disabled className="h-9 bg-muted/40 tabular-nums text-center" value={nights} data-testid="mockup-datos-noches" />
              </Field>
              <Field label="Régimen" span="sm:col-span-2">
                <Select value={state.board} onValueChange={(v) => set("board", v)}>
                  <SelectTrigger className="h-9" data-testid="mockup-datos-regimen"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RO">Solo hab. (RO)</SelectItem>
                    <SelectItem value="BB">Desayuno (BB)</SelectItem>
                    <SelectItem value="HB">Media pensión (HB)</SelectItem>
                    <SelectItem value="FB">Pensión completa (FB)</SelectItem>
                    <SelectItem value="AI">Todo incluido (AI)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="sm:col-span-2 flex flex-col">
                <Label className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Flag className="h-3 w-3" /> Flag
                </Label>
                <ToggleRow icon={Flag} label="Flag" checked={state.flag} onChange={(v) => set("flag", v)} dataTestid="mockup-datos-flag" />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-12" data-testid="mockup-datos-fila3">
              <Field icon={Circle} label="Día" span="sm:col-span-4">
                <Input type="date" className="h-9" value={state.date_entry} onChange={(e) => set("date_entry", e.target.value)} data-testid="mockup-datos-dia" />
              </Field>
              <Field icon={Circle} label="Hora" span="sm:col-span-4">
                <Input className="h-9" value={state.hour_entry} placeholder="14:15" onChange={(e) => set("hour_entry", e.target.value)} data-testid="mockup-datos-hora" />
              </Field>
              <div className="sm:col-span-4 flex flex-col">
                <Label className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Flag className="h-3 w-3" /> Flag
                </Label>
                <ToggleRow icon={Flag} label="Flag" checked={state.flag} onChange={(v) => set("flag", v)} dataTestid="mockup-datos-flag" />
              </div>
            </div>
          )}

          <Separator />

          {/* Fila 4 · Estados / Facturación — SIEMPRE 5 columnas iguales:
              Estado Facturación (SELECT) + 4 toggles equiespaciados.
              Consistente para todos los tipos de servicio. */}
          <div>
            <Label className="mb-2 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Estados y Facturación
            </Label>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <div>
                <Select value={state.status_facturacion} onValueChange={(v) => set("status_facturacion", v)}>
                  <SelectTrigger className="h-9" data-testid="mockup-datos-estado-fact">
                    <span className="mr-1 inline-flex items-center text-muted-foreground">
                      <Receipt className="h-3.5 w-3.5" />
                    </span>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(FACT_PALETTE).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <ToggleRow icon={FileSignature} label="Fact. Solicitada" checked={state.factura_solicitada} onChange={(v) => set("factura_solicitada", v)} dataTestid="mockup-datos-fact-sol" />
              <ToggleRow icon={ScrollText} label="Proforma/Voucher" checked={state.status_proforma_voucher} onChange={(v) => set("status_proforma_voucher", v)} dataTestid="mockup-datos-proforma" />
              <ToggleRow icon={ShieldCheck} label="Conciliado" checked={state.status_conciliado} onChange={(v) => set("status_conciliado", v)} dataTestid="mockup-datos-conciliado" />
              <ToggleRow icon={Coins} label="Estado Pago" checked={state.status_pago} onChange={() => {}} disabled dataTestid="mockup-datos-pago" />
            </div>
          </div>

          {/* Notas */}
          <div>
            <Label className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <StickyNote className="h-3 w-3" /> Notas
            </Label>
            <Textarea rows={3} value={state.notes} onChange={(e) => set("notes", e.target.value)} data-testid="mockup-datos-notas" />
          </div>
        </div>
      </section>

      {/* ═══════════════════════ MÓDULO 2 · COTIZACIÓN (colapsable) ═══════════════════════ */}
      <Collapsible open={cotOpen} onOpenChange={setCotOpen} className="rounded-lg border border-emerald-500/25 bg-card shadow-sm" data-testid="mockup-modulo-cotizacion">
        <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 border-b bg-gradient-to-r from-emerald-500/[0.06] to-transparent px-5 py-3 text-left">
          <div className="flex items-center gap-2">
            <Calculator className="h-4 w-4 text-emerald-600" />
            <h2 className="font-heading text-base font-bold">Cotización</h2>
            <span className="text-xs text-muted-foreground">· colapsable</span>
          </div>
          <span className="flex items-center gap-3">
            <span className="text-sm font-bold tabular-nums text-emerald-700">{fmtEUR(state.cotizado_real)}</span>
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
          </span>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="space-y-5 p-5">
            {/* Fila 1: Unidades · Métrica · Modelo Cálculo */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-12">
              <Field label="Unidades" span="sm:col-span-3">
                <Input type="number" min="1" className="h-9 tabular-nums" value={state.units} onChange={(e) => set("units", e.target.value)} data-testid="mockup-cot-und" />
              </Field>
              <Field label="Moneda" span="sm:col-span-4">
                <Select value={state.currency} onValueChange={(v) => set("currency", v)}>
                  <SelectTrigger className="h-9" data-testid="mockup-cot-moneda"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">€ · Euro (EUR)</SelectItem>
                    <SelectItem value="USD">$ · Dólar (USD)</SelectItem>
                    <SelectItem value="GBP">£ · Libra (GBP)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Modelo Cálculo" span="sm:col-span-5">
                <Select value={state.override} onValueChange={(v) => set("override", v)}>
                  <SelectTrigger className="h-9" data-testid="mockup-cot-modelo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no">Estándar (markup partner)</SelectItem>
                    <SelectItem value="manual">Precio manual (override)</SelectItem>
                    <SelectItem value="neto">Neto (sin markup)</SelectItem>
                    <SelectItem value="incluido">Incluido en otro concepto</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            {/* Fila 2: Cotizado K/E (izquierda) · Coste Real Total con Sin IVA / Con IVA (derecha) */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-12">
              <div className="sm:col-span-5 rounded-md border bg-muted/20 p-3">
                <Label className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Calculator className="h-3 w-3" /> Cotizado K/E (manual)
                </Label>
                <Input type="number" step="0.01" className="h-9 text-right tabular-nums" value={state.cotizado_kk} onChange={(e) => set("cotizado_kk", e.target.value)} data-testid="mockup-cot-cotizado-kk" />
                <p className="mt-1.5 text-[10px] text-muted-foreground">Fuerza el precio cotizado por unidad si lo necesitas.</p>
              </div>

              <div className="sm:col-span-7 rounded-md border bg-muted/20 p-3">
                <Label className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Receipt className="h-3 w-3" /> Coste Real Total (proveedor)
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Sin IVA">
                    <Input type="number" step="0.01" className="h-9 text-right tabular-nums" value={state.cost_excl} onChange={(e) => set("cost_excl", e.target.value)} data-testid="mockup-cot-sin-iva" />
                  </Field>
                  <Field label="Con IVA">
                    <Input type="number" step="0.01" className="h-9 text-right tabular-nums" value={state.cost_incl} onChange={(e) => set("cost_incl", e.target.value)} data-testid="mockup-cot-con-iva" />
                  </Field>
                </div>
                <div className="mt-3 flex items-center justify-between border-t pt-2 text-xs">
                  <span className="text-muted-foreground">Total × {state.units} ud.</span>
                  <span className="tabular-nums">
                    <strong>{fmtEUR(state.cost_excl * state.units)}</strong> <span className="text-muted-foreground">s/IVA · </span>
                    <strong>{fmtEUR(state.cost_incl * state.units)}</strong> <span className="text-muted-foreground">c/IVA</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* ═══════════════════════ TABS ACTUALES (preservadas) ═══════════════════════ */}
      <div className="pt-2">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Sub-pestañas de la reserva (sin cambios)</p>
        <Tabs defaultValue="pagos">
          <TabsList className="w-full flex-wrap">
            <TabsTrigger value="pagos">Pagos</TabsTrigger>
            <TabsTrigger value="documentos">Documentos</TabsTrigger>
            <TabsTrigger value="factura">Factura IA</TabsTrigger>
            <TabsTrigger value="solicitud">Solicitud</TabsTrigger>
            <TabsTrigger value="vouchers">Vouchers</TabsTrigger>
            <TabsTrigger value="recordatorios">Recordatorios</TabsTrigger>
            <TabsTrigger value="template">Template</TabsTrigger>
            <TabsTrigger value="chat">Chat</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="mt-3 rounded-lg border border-dashed p-6 text-center text-xs text-muted-foreground">
          Placeholder — el contenido de cada pestaña se mantiene tal cual está hoy.
        </div>
      </div>
    </div>
  );
}
